import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Layout from '../components/Layout'
import InstallPrompt from '../components/InstallPrompt'
import { getOrder, SERVICE_LABEL } from '../lib/orders'
import { getService } from '../lib/catalog'
import { fmtMoney } from '../lib/pricing'
import { useAuth } from '../context/AuthContext'
import { formatAddress } from '../lib/auth'
import { shortDate } from '../lib/dates'

function slotLabel(id) {
  if (id === 'morning') return '9–11 AM'
  if (id === 'evening') return '6–9 PM'
  return ''
}

export default function Confirmation() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { profile } = useAuth()
  const orderNumber = params.get('id')
  const [order, setOrder] = useState(null)
  const [service, setService] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!orderNumber) {
      setLoading(false)
      return
    }
    let alive = true
    ;(async () => {
      try {
        const row = await getOrder(orderNumber)
        if (!alive) return
        setOrder(row)
        // Read the service row so every label comes from the source of
        // truth. No prices are hard-coded in this file.
        if (row?.service_id) {
          const svc = await getService(row.service_id).catch(() => null)
          if (alive) setService(svc)
        }
      } catch (err) {
        if (!alive) return
        setError(err.message || 'Could not load order.')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [orderNumber])

  const address = formatAddress(profile)

  if (loading) {
    return (
      <Layout>
        <div className="p-6 text-center text-[13px] text-ink-2">Loading order…</div>
      </Layout>
    )
  }

  if (!order || error) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-[13px] text-ink-2">
            {error || 'No order found.'}
          </p>
          <Link to="/order" className="mt-3 inline-block text-[13px] font-bold text-aqua">
            Book a pickup →
          </Link>
        </div>
      </Layout>
    )
  }

  const serviceLabel = service?.name || SERVICE_LABEL[order.service] || order.service
  // Min-order label derived from the service row. Only meaningful for
  // per_pound; per_item and subscription don't carry a per-order minimum.
  const minOrderLabel = service?.pricing_mode === 'per_pound'
    ? fmtMoney(service.base_price_cents)
    : null
  const pickupText = `${shortDate(order.pickup_date)} · ${slotLabel(order.pickup_slot)}`
  const returnText = order.return_tbd
    ? 'TBD — confirmed when your order is ready'
    : order.return_date
    ? `${shortDate(order.return_date)} · ${slotLabel(order.return_slot)}`
    : 'TBD'

  return (
    <Layout>
      <div className="flex flex-col items-center px-5 pb-12 pt-6">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-green text-[42px] font-extrabold text-white">
          ✓
        </div>
        <h1 className="font-bricolage text-[22px] font-extrabold text-navy">Pickup booked!</h1>
        <p className="mt-2 max-w-[280px] text-center text-[13px] leading-relaxed text-ink-2">
          See you {shortDate(order.pickup_date)} between {slotLabel(order.pickup_slot)}
          {address.line1 ? ` at ${address.line1}` : ''}
          {!order.return_tbd && order.return_date
            ? `. Return ${shortDate(order.return_date)} ${slotLabel(order.return_slot)}.`
            : '.'}
        </p>

        <div className="mt-6 w-full space-y-2 rounded-xl border border-line bg-[#F9FAFB] p-3.5 text-[12px]">
          <Row label="Order" value={`#${order.order_number || order.id}`} />
          <Row label="Service" value={`${serviceLabel} · ${order.bags} bag${order.bags === '1' ? '' : 's'}`} />
          <Row label="Pickup" value={pickupText} />
          <Row label="Return" value={returnText} />
          {minOrderLabel && (
            <div className="border-t border-line pt-2">
              <Row label="Min order" value={minOrderLabel} bold />
            </div>
          )}
        </div>

        {/* Pickup address — visible so the customer can double-check + edit. */}
        <div className="mt-3 w-full rounded-xl border border-line bg-white p-3.5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold uppercase tracking-wide text-ink-2">
                📍 Pickup address
              </div>
              {address.full || order.hotel_name ? (
                <div className="mt-1 text-[12px] leading-tight text-navy">
                  {order.hotel_name && (
                    <div className="font-bold">
                      {order.hotel_name}
                      {order.room_number ? ` · Room ${order.room_number}` : ''}
                    </div>
                  )}
                  <div className={order.hotel_name ? 'text-ink-2' : 'font-bold'}>
                    {address.line1}
                  </div>
                  <div className="text-ink-2">{address.line2}</div>
                </div>
              ) : (
                <div className="mt-1 text-[12px] italic text-ink-3">
                  No address on file yet — add one in your profile.
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="text-[11px] font-extrabold text-aqua"
            >
              Edit
            </button>
          </div>
          <p className="mt-2 border-t border-line pt-2 text-[10px] text-ink-3">
            Need to change it for this pickup only? Text (954) 900-9969 with your order # and the new address.
          </p>
        </div>

        {order.notes && (
          <div className="mt-3 w-full rounded-xl border border-line bg-white p-3.5 text-[12px] text-navy">
            <div className="text-[11px] font-bold uppercase tracking-wide text-ink-2">Notes</div>
            <p className="mt-1">{order.notes}</p>
          </div>
        )}

        <div className="mt-6 w-full rounded-xl border border-line bg-[#F9FAFB] p-3.5 text-[12px] leading-relaxed text-ink-2">
          We&apos;ll weigh your bags, price your order, then reach out to settle it.
          Nothing to pay right now.
        </div>

        <button
          type="button"
          onClick={() => navigate('/order')}
          className="mt-6 text-[12px] font-bold text-aqua"
        >
          Back to home
        </button>
      </div>
      <InstallPrompt />
    </Layout>
  )
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? 'font-bold text-navy' : 'text-ink-2'}>{label}</span>
      <span className={bold ? 'font-bricolage text-[13px] font-extrabold text-navy' : 'text-navy'}>{value}</span>
    </div>
  )
}
